import { Component } from '@theme/component';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Framer-style ticker. Clones the first [data-mrk-marquee-group] until the track covers the viewport twice,
 * then loops it with the Web Animations API at `data-speed` px/s. `data-hover-speed` (0–1) slows it on hover.
 * Under prefers-reduced-motion it stays static (the track becomes horizontally scrollable via CSS).
 */
class MrkMarquee extends Component {
  /** @type {Animation | undefined} */
  #animation;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;
  #width = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#slow);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    reducedMotion.addEventListener('change', this.#setup);
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.clientWidth !== this.#width) this.#setup();
    });
    this.#resizeObserver.observe(this);
    this.#visibilityObserver = new IntersectionObserver(([entry]) => {
      if (!this.#animation) return;
      if (entry?.isIntersecting) this.#animation.play();
      else this.#animation.pause();
    });
    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    reducedMotion.removeEventListener('change', this.#setup);
    this.#resizeObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#animation?.cancel();
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-mrk-marquee-track]'));
  }

  #setup = () => {
    const track = this.#track;
    const group = track?.querySelector('[data-mrk-marquee-group]');
    if (!track || !(group instanceof HTMLElement)) return;

    this.#width = this.clientWidth;
    this.#animation?.cancel();
    this.#animation = undefined;
    track.querySelectorAll('[data-mrk-marquee-clone]').forEach((clone) => clone.remove());

    if (reducedMotion.matches) {
      this.toggleAttribute('data-static', true);
      return;
    }
    this.removeAttribute('data-static');

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = group.getBoundingClientRect().width + gap;
    if (distance <= gap) return;

    const copies = Math.ceil((this.#width * 2) / distance);
    for (let i = 0; i < copies; i++) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
      clone.setAttribute('data-mrk-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      track.append(clone);
    }

    const speed = Number(this.dataset.mrkSpeed) || 30;
    this.#animation = track.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-distance}px)` }],
      { duration: (distance / speed) * 1000, iterations: Infinity }
    );
  };

  #slow = () => {
    const rate = Number(this.dataset.mrkHoverSpeed);
    if (this.#animation && rate > 0 && rate < 1) this.#animation.updatePlaybackRate(rate);
  };

  #pause = () => this.#animation?.pause();

  #resume = () => {
    if (!this.#animation || this.matches(':focus-within')) return;
    this.#animation.updatePlaybackRate(1);
    this.#animation.play();
  };
}

/**
 * Horizontal scroll rail with previous/next buttons (credits). Buttons hide when nothing overflows.
 * Refs: `rail`, `previous`, `next`.
 */
class MrkScroller extends Component {
  requiredRefs = ['rail'];
  /** @type {HTMLElement | undefined} */
  #rail;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    const { rail } = this.refs;
    if (!(rail instanceof HTMLElement)) return;
    this.#rail = rail;
    rail.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(rail);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#rail?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#rail = undefined;
    this.#resizeObserver = undefined;
  }

  /** @param {Event} event */
  scrollNext(event) {
    this.#scroll(event, 1);
  }

  /** @param {Event} event */
  scrollPrevious(event) {
    this.#scroll(event, -1);
  }

  /**
   * @param {Event} _event
   * @param {number} direction
   */
  #scroll(_event, direction) {
    const rail = /** @type {HTMLElement} */ (this.refs.rail);
    const item = rail.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0) : rail.clientWidth;
    rail.scrollBy({ left: step * direction, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  #update = () => {
    const rail = /** @type {HTMLElement} */ (this.refs.rail);
    const max = rail.scrollWidth - rail.clientWidth;
    this.toggleAttribute('data-overflowing', max > 2);
    const { previous, next } = this.refs;
    if (previous instanceof HTMLButtonElement) previous.disabled = rail.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = rail.scrollLeft >= max - 2;
  };
}

if (!customElements.get('mrk-marquee')) customElements.define('mrk-marquee', MrkMarquee);
if (!customElements.get('mrk-scroller')) customElements.define('mrk-scroller', MrkScroller);
